import { useTranslation } from 'react-i18next';
import { Alert } from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useWindowTitle } from '@/shared/windowing';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import { useGetProductsPlaceholder } from '@/api/generated/products-placeholders/products-placeholders';
import { placeholderDetailAgenda } from './placeholderDetailAgenda';
import { PlaceholderDetailView } from './PlaceholderDetailView';
import { emptyPlaceholderFormValues, fromGetPlaceholderResponse } from './placeholderFormSchema';

interface PlaceholderDetailPageProps {
  layerId: string;
}

/** Thin loader: vyřeší parametr agendy a (v editaci) načtení detailu placeholderu. */
export function PlaceholderDetailPage({ layerId }: PlaceholderDetailPageProps) {
  const { t } = useTranslation('venua-planner');
  const { open } = useAgendaParams(placeholderDetailAgenda, layerId);
  const placeholderId = open.placeholderId;
  const isEdit = placeholderId != null;

  const detailQuery = useGetProductsPlaceholder(placeholderId ?? '', {
    query: { enabled: isEdit, staleTime: DETAIL_QUERY_STALE_TIME_MS },
  });

  // Dynamický název okna (taskbar / historie / hlavička) = název placeholderu (v editaci; při zakládání
  // zůstane statický titulek agendy).
  useWindowTitle(detailQuery.data?.name);

  if (isEdit && detailQuery.isLoading) {
    return (
      <p className="text-phx-text-muted text-phx-sm p-phx-5">{t('schemes.placeholder.loading')}</p>
    );
  }
  if (isEdit && detailQuery.isError) {
    return (
      <div className="p-phx-5">
        <Alert severity="error" title={t('schemes.placeholder.loadErrorTitle')}>
          {t('schemes.placeholder.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <PlaceholderDetailView
      layerId={layerId}
      placeholderId={placeholderId}
      initialValues={
        detailQuery.data
          ? fromGetPlaceholderResponse(detailQuery.data)
          : emptyPlaceholderFormValues()
      }
    />
  );
}
